import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type TalkdownOutput = { id: string; display: string };

type UseTalkdownDeviceSelectionResult = {
  selectedDevices: string[];
  toggleDevice: (deviceId: string) => void;
  selectAllDevices: () => void;
  clearAllDevices: () => void;
};

export const useTalkdownDeviceSelection = (
  outputs?: TalkdownOutput[],
  defaultValue?: string[]
): UseTalkdownDeviceSelectionResult => {
  const [selectedDevices, setSelectedDevices] = useState<string[]>(defaultValue ?? []);

  const hasInitializedSelectionRef = useRef(defaultValue?.length ?? false);
  const previousAvailableIdsRef = useRef<string[]>([]);

  const availableIds = useMemo(
    () => outputs?.map((output) => output.id) ?? [],
    [outputs]
  );

  useEffect(() => {
    if (availableIds.length === 0) {
      previousAvailableIdsRef.current = [];
      return;
    }

    const availableIdSet = new Set(availableIds);

    setSelectedDevices((prev) => {
      if (!hasInitializedSelectionRef.current) {
        hasInitializedSelectionRef.current = true;
        previousAvailableIdsRef.current = availableIds;
        return availableIds;
      }

      const prevAvailableIds = previousAvailableIdsRef.current;
      const prevSelectedSet = new Set(prev);
      const previouslySelectedAll =
        prevAvailableIds.length > 0 &&
        prev.length === prevAvailableIds.length &&
        prevAvailableIds.every((id) => prevSelectedSet.has(id));

      if (previouslySelectedAll) {
        previousAvailableIdsRef.current = availableIds;
        return availableIds;
      }

      const next = prev.filter((id) => availableIdSet.has(id));
      const unchanged =
        next.length === prev.length &&
        next.every((id, index) => id === prev[index]);

      previousAvailableIdsRef.current = availableIds;

      if (unchanged) {
        return prev;
      }

      return next;
    });
  }, [availableIds]);

  const toggleDevice = useCallback((deviceId: string) => {
    setSelectedDevices((prev) => {
      if (prev.includes(deviceId)) {
        const filtered = prev.filter((id) => id !== deviceId);
        return filtered;
      }
      return [...prev, deviceId];
    });
  }, []);

  const selectAllDevices = useCallback(() => {
    if (availableIds.length === 0) {
      return;
    }

    setSelectedDevices((prev) => {
      const unchanged =
        prev.length === availableIds.length &&
        prev.every((id, index) => id === availableIds[index]);

      if (unchanged) {
        return prev;
      }

      return availableIds;
    });
  }, [availableIds]);

  const clearAllDevices = useCallback(() => {
    setSelectedDevices((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      return [];
    });
  }, []);

  return {
    selectedDevices,
    toggleDevice,
    selectAllDevices,
    clearAllDevices,
  };
};
